import { IScheduler } from "../../../utils/scheduler";
import { ITaskOptions, Service } from "../../serviceBase";
import { HOURS } from "../../../constants";
import * as logger from "../../../utils/logger";
import { DriveStore } from "../../../store/reducers";
import Api from "../../../services/api";
import { handleApi } from "../../../utils/error";
import { CoachingActions } from "../store/actions";
import { CoachingSessionService } from "./coachingSessionService";
import { UserService } from "../../../utils/users";
import { SecurityService } from "../../../utils/security";
import { IFeatureFlagsService } from "../../../services/featureFlags";
import { FeatureFlagName } from "../../../utils/featureFlagName";

export default class ScheduledCoachingService extends Service {

    options: ITaskOptions = {
        name: "@scheduledCoachingService",
        msInterval: 12 * HOURS,
        hasDelayAfterStart: true,
        registerOnceForAllUsers: false,
        onDemand: false,
        startingInterval: window.DRIVE_DEBUG?.STARTING_INTERVAL || Math.floor(Math.random() * (10 - 1 + 1) + 1) * 1000,
        delayByInterval: false
    };

    private readonly coachingSessionService: CoachingSessionService;

    constructor(
        private readonly store: DriveStore,
        private readonly api: Api,
        protected readonly scheduler: IScheduler,
        private readonly userService: UserService,
        private readonly securityService: SecurityService,
        private readonly featureFlagsService: IFeatureFlagsService,
        coachingSessionService?: CoachingSessionService
    ) {
        super(scheduler);
        this.userName = this.api.getUser()?.name;
        this.coachingSessionService = coachingSessionService ?? new CoachingSessionService(this.api);
        this.registerTask(this.check.bind(this), this.options, this.userName);
    }

    check() {
        const currentUser = this.api.getUser();
        if (!this.canOpen(currentUser)) {
            return;
        }

        logger.log(logger.Source.SERVICES, "ScheduledCoachingService is running");

        const userId = currentUser.id;

        return this.coachingSessionService.getCoachingSessions()
            .then(sessions => {
                if (!sessions.length) {
                    logger.log(logger.Source.SERVICES, "No coaching sessions available for user - clearing the coaching data store", currentUser);
                    this.store.dispatch(CoachingActions.clearCoachingState(userId));
                } else {
                    this.store.dispatch(CoachingActions.setCoachingSessions({ userId, sessions }));
                    logger.log(logger.Source.SERVICES, "ScheduledCoachingService coaching store updated", currentUser);
                }
                return Promise.resolve();
            })
            .catch(handleApi);
    }

    destroy(): void {
        super.destroy();
    }

    private canOpen(user: IUser | null): user is IUser {
        if (!user) {
            return false;
        }
        const hasClearance = this.securityService.can("viewCoachingSession", user.id)
            || this.securityService.can("manageCoachingSession", user.id);
        return hasClearance
            && this.featureFlagsService.hasFeatureFlag(FeatureFlagName.ENABLE_COACHING_MODULE)
            && this.userService.isDriverUsingUI(user.name || "");
    }
}
